import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import {
  TenantCreateFormData,
  tenantCreateSchema,
  TenantUpdateFormData,
  tenantUpdateSchema,
} from "../../schemas/tenantSchema";
import Button from "../common/Button";

interface TenantFormProps {
  initialData?: Partial<TenantUpdateFormData>;
  onSubmit: (data: TenantCreateFormData | TenantUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
  isOpen?: boolean;
}

const TenantForm: React.FC<TenantFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
  isOpen = true,
}) => {
  const form = useForm<TenantCreateFormData | TenantUpdateFormData>({
    resolver: zodResolver(isUpdate ? tenantUpdateSchema : tenantCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      isPremium: false,
      ...initialData,
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    trigger,
  } = form;

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset({
          companyName: initialData.companyName || "",
          username: initialData.username || "",
          email: initialData.email || "",
          phoneNumber: initialData.phoneNumber || "",
          address: initialData.address || "",
          isPremium: initialData.isPremium || false,
        });
      } else {
        reset({
          companyName: "",
          username: "",
          email: "",
          phoneNumber: "",
          address: "",
          isPremium: false,
        });
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Company Name"
          placeholder="Enter company name"
          name="companyName"
          value={watch("companyName") || ""}
          onChange={(e) =>
            setValue("companyName", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("companyName")}
          error={errors.companyName}
        />
        <Input
          label="Username"
          placeholder="Enter username"
          name="username"
          value={watch("username") || ""}
          onChange={(e) =>
            setValue("username", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("username")}
          error={errors.username}
        />
        <Input
          label="Email"
          placeholder="Enter email"
          name="email"
          value={watch("email") || ""}
          onChange={(e) =>
            setValue("email", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("email")}
          error={errors.email}
        />
        <Input
          label="Phone Number"
          placeholder="Enter phone number"
          name="phoneNumber"
          value={watch("phoneNumber") || ""}
          onChange={(e) =>
            setValue("phoneNumber", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("phoneNumber")}
          error={errors.phoneNumber}
          allowedChars="numeric"
        />
        <Input
          label="Address"
          placeholder="Enter address"
          name="address"
          value={watch("address") || ""}
          onChange={(e) =>
            setValue("address", e.target.value, { shouldValidate: false })
          }
          onBlur={() => trigger("address")}
          error={errors.address}
        />
        {/* <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="isPremium"
            checked={watch("isPremium") || false}
            onChange={(e) => setValue("isPremium", e.target.checked)}
            className="w-4 h-4 text-primary-default border-gray-300 rounded focus:ring-primary-default"
          />
          <label
            htmlFor="isPremium"
            className="text-sm font-medium text-gray-700"
          >
            Premium Tenant
          </label>
        </div>
        {errors.isPremium && (
          <span className="text-red-500 text-sm">
            {errors.isPremium.message}
          </span>
        )} */}
      </div>
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          {isUpdate ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export default TenantForm;
